import { Input, Form, Button, notification } from "antd";
import type { FormProps } from "antd";
import { LoginUserBasicResponse, LoginUserChallengeResponse } from "@microtica/ms-usermanagement-sdk";
import { GithubOutlined } from '@ant-design/icons';
import { Mail, Lock, ArrowRight, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { getUserManagementService } from "../../backend";
import { useNavigate } from "react-router";
import { useAuth } from "../../contexts/Auth";
import { Link } from "react-router-dom";
import MicroticaLogoColor from "../../assets/logo-color.svg";
import AuthVisualPanel from "../../components/auth/AuthVisualPanel";

// Google logo SVG component
const GoogleLogo = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
);

type RequiredMark = FormProps['requiredMark'];

export default function Login() {
    const [form] = Form.useForm();
    const { isLoggedIn } = useAuth();
    const navigate = useNavigate();
    const { loginWithExternalProvider, processAuthData } = useAuth();
    const [loading, setLoading] = useState(false);
    const [requiredMark] = useState<RequiredMark>('optional');

    useEffect(() => {
        if (isLoggedIn) {
            navigate("/projects");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    async function handleLogin(values: { email: string; password: string }) {
        try {
            setLoading(true);
            const { data } = (await getUserManagementService().loginUser({
                username: values.email,
                password: values.password
            })) as { data: LoginUserBasicResponse | LoginUserChallengeResponse };

            const { done, challengeName, sessionToken } = data as LoginUserChallengeResponse;

            if (!done && challengeName === "NEW_PASSWORD_REQUIRED") {
                navigate(`/auth/set-password/${values.email}/${sessionToken}`);
            } else {
                await processAuthData(data as LoginUserBasicResponse, "email");
            }

        } catch (error: any) {
            notification.error({
                message: "Unable to Login",
                description: error.response.data.message
            });
        } finally {
            setLoading(false);
        }
    }

    const handleLoginWithGitHub = async () => {
        await loginWithExternalProvider("github", "login");
    }

    const handleLoginWithGoogle = async () => {
        await loginWithExternalProvider("google", "login");
    }

    return (
        <div className="login-container">
            {/* Left side - Visual/Brand section */}
            <AuthVisualPanel />

            {/* Right side - Login form */}
            <div className="login-form-section">
                <div className="login-form-wrapper">
                    {/* Mobile logo */}
                    <div className="login-mobile-brand">
                        <Link to="/">
                            <img src={MicroticaLogoColor} alt="Microtica" />
                        </Link>
                    </div>

                    <div className="login-form-header">
                        <h2 className="login-form-title">Welcome back</h2>
                        <p className="login-form-subtitle">Sign in to continue to your dashboard</p>
                    </div>

                    <div className="login-providers">
                        <button 
                            className="login-provider-btn login-provider-github" 
                            onClick={handleLoginWithGitHub}
                            type="button"
                        >
                            <GithubOutlined />
                            <span>GitHub</span>
                        </button>
                        <button 
                            className="login-provider-btn login-provider-google" 
                            onClick={handleLoginWithGoogle}
                            type="button"
                        >
                            <GoogleLogo />
                            <span>Google</span>
                        </button>
                    </div>

                    <div className="login-divider">
                        <span>or continue with email</span>
                    </div>

                    <Form
                        form={form}
                        layout="vertical"
                        requiredMark={requiredMark}
                        onFinish={handleLogin}
                        className="login-form"
                    >
                        <Form.Item
                            name="email"
                            required
                            rules={[
                                { required: true, message: `Please enter email address` },
                                { pattern: /^\S+@\S+\.\S+$/, message: "Please enter a valid email address" }
                            ]}
                        >
                            <Input 
                                placeholder="Email address" 
                                prefix={<Mail size={18} strokeWidth={1.5} />}
                                className="login-input"
                                size="large"
                            />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            required
                            rules={[{ required: true, message: `Please input password!` }]}
                        >
                            <Input.Password 
                                placeholder="Password" 
                                prefix={<Lock size={18} strokeWidth={1.5} />}
                                className="login-input"
                                size="large"
                            />
                        </Form.Item>

                        <div className="login-forgot">
                            <Link to="/auth/reset-password">Forgot password?</Link>
                        </div>

                        <Button 
                            htmlType="submit" 
                            type="primary" 
                            block 
                            size="large"
                            disabled={loading}
                            className={`login-submit-btn ${loading ? 'login-submit-btn-loading' : ''}`}
                        >
                            {loading ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    <span>Signing in...</span>
                                </>
                            ) : (
                                <>
                                    <span>Sign in</span>
                                    <ArrowRight size={18} className="login-submit-arrow" />
                                </>
                            )}
                        </Button>
                    </Form>

                    <div className="login-signup-link">
                        Don't have an account? <Link to="/auth/signup">Create one</Link>
                    </div>
                </div>

                <div className="login-footer">
                    <span>© {new Date().getFullYear()} Microtica. All rights reserved.</span>
                </div>
            </div>
        </div>
    )
}
